import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Stack,
  Divider,
  IconButton,
  TextField,
  Button,
  Popover,
  List,
  ListItemButton,
  ListItemText,
} from "@mui/material";
import {
  addDays,
  startOfMonth,
  endOfMonth,
  subMonths,
  startOfYear,
  endOfYear,
} from "date-fns";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import QueryStatsIcon from "@mui/icons-material/QueryStats";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import ForumOutlinedIcon from "@mui/icons-material/ForumOutlined";
import SmsOutlinedIcon from "@mui/icons-material/SmsOutlined";
import BoltIcon from "@mui/icons-material/Bolt";
import EventNoteIcon from "@mui/icons-material/EventNote";
import RefreshIcon from "@mui/icons-material/Refresh";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import FilterListIcon from "@mui/icons-material/FilterList";
import Fade from "@mui/material/Fade";
import { useTheme } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import { buildLeadDetailReturn } from "../utils/leadDetailNavigation";
import api from "../utils/api";
import { subscribeLeadUpdates } from "../utils/leadSync";
import useAuth from "../hooks/useAuth";
import useLandscape from "../hooks/useLandscape";
import useTour from "../tour/useTour";

// step definitions
import { resolveDashboardSteps } from "../tour/resolveDashboardSteps";
import { isMainNavTourComplete, MAIN_NAV_TOUR_KEY } from "../tour/tourKeys";
import { canAccessRoleSettings } from "../guidedTour/narrationScripts";
import { getDashboardGreeting } from "../utils/greeting";
import { lightTheme } from "../theme";
import {
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Legend,
} from "recharts";

/** Brand hero colors — fixed in dark mode (dark theme uses a lighter primary). */
const DASHBOARD_HERO_BLUE_MAIN = lightTheme.palette.primary.main;
const DASHBOARD_HERO_BLUE_DARK = lightTheme.palette.primary.dark;

// --------------------------------------------------
// COLOR PALETTE
// --------------------------------------------------

const STATUS_COLORS = {
  new: "#42A5F5", // Blue
  "follow up needed": "#FB8C00", // Orange (3 word variant)
  "appointment scheduled": "#8DD7BF",
  "not home": "#9E9E9E", // Gray
  renter: "#7B1FA2", // Purple
  "not interested": "#E53935", // Red
  vacant: "#8D6E63", // Brown
  "closed won": "#43A047", // Green
  "competitor installed": "#7189AD",
  "adt installed": "#567EC8",
};

/** Local calendar YYYY-MM-DD (not UTC) — aligns with dashboard "today" through end of local day. */
function formatLocalYMD(d) {
  if (!d || !(d instanceof Date) || Number.isNaN(d.getTime())) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/** Matches Sales Pipeline territory filter button + popover styling. */
const DASHBOARD_SCOPE_FILTER_BUTTON_SX = {
  borderRadius: "10px",
  borderWidth: 1,
  borderStyle: "solid",
  borderColor: "primary.main",
  px: 2,
  textTransform: "none",
  fontWeight: 600,
  flexShrink: 0,
  maxWidth: "100%",
  "& .MuiButton-startIcon": { flexShrink: 0 },
  "&:hover": {
    bgcolor: "primary.main",
    color: "primary.contrastText",
    borderWidth: 1,
    borderStyle: "solid",
    borderColor: "primary.main",
  },
};

const DASHBOARD_SCOPE_FILTER_POPOVER_PAPER_SX = {
  mt: 0.75,
  minWidth: 260,
  maxWidth: 360,
  borderRadius: 2,
  boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
};

const DASHBOARD_RESET_FILTER_BUTTON_SX = {
  textTransform: "none",
  "&:hover": {
    bgcolor: "primary.main",
    color: "primary.contrastText",
    borderColor: "primary.main",
  },
};

const RANGE_OPTIONS = [
  { value: "allTime", label: "All Time" },
  { value: "today", label: "Today" },
  { value: "7", label: "Last 7 Days" },
  { value: "30", label: "Last 30 Days" },
  { value: "thisMonth", label: "This Month" },
  { value: "lastMonth", label: "Last Month" },
  { value: "thisYear", label: "This Year" },
  { value: "custom", label: "Custom Range" },
];

// --------------------------------------------------
// METRIC CARD
// --------------------------------------------------
const MetricCard = ({ label, value, icon }) => (
  <Card
  sx={{
    height: "100%",
    borderRadius: 3,
    boxShadow: (theme) =>
      theme.palette.mode === "dark"
        ? "0 6px 16px rgba(0,0,0,0.55)"
        : "0 4px 12px rgba(0,0,0,0.12)",
  }}
>

    <CardContent>
      <Stack direction="row" spacing={2} alignItems="center">
        <IconButton size="small" sx={{ bgcolor: "action.hover" }} disabled>
          {icon}
        </IconButton>
        <Box>
          <Typography variant="body2" color="text.secondary">
            {label}
          </Typography>
          <Typography variant="h6" fontWeight={700}>
            {value}
          </Typography>
        </Box>
      </Stack>
    </CardContent>
  </Card>
);

// --------------------------------------------------
// MAIN DASHBOARD COMPONENT
// --------------------------------------------------
export default function Dashboard() {
  const theme = useTheme();
  const { role, user } = useAuth();
  // Stable primitive — `user` from useAuth is a new object every render (JSON.parse), which must
  // not appear in useCallback deps or effects will loop and spam the API.
  const authUserId = user?.id != null ? Number(user.id) : null;
  const { startTour } = useTour();
  const navigate = useNavigate();

  const isLandscape = useLandscape();
  const [metrics, setMetrics] = useState({
    // Step 1 metrics: Territory-scoped totals (from /performance/summary/ totals)
    total_leads: 0,
    active_leads: 0,
    leads_closed: 0,
    follow_ups: 0,
    // Legacy/UI metrics
    activeReps: 0,
    // Step 2 metrics: Rep performance (from /performance/summary/ leaderboard)
    conversion_rate: 0,
  });

  const [series, setSeries] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [statusData, setStatusData] = useState([]);
  const [statusDistributionMeta, setStatusDistributionMeta] = useState({
    mode: "snapshot",
    leadsTouched: null,
  });
  const [territories, setTerritories] = useState([]);
  const [selectedTerritory, setSelectedTerritory] = useState("");
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState("");
  const [loading, setLoading] = useState(true);
  const [activityItems, setActivityItems] = useState([]);
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [selectedRange, setSelectedRange] = useState("allTime");
  const [leadStatusTerritoryAnchorEl, setLeadStatusTerritoryAnchorEl] =
    useState(null);
  const [teamPerformanceUserAnchorEl, setTeamPerformanceUserAnchorEl] =
    useState(null);

  const dashboardTimeZone = useMemo(
    () => Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC",
    []
  );

  const dashboardContainerSx = {
  borderRadius: 3,

  bgcolor: (theme) =>
    theme.palette.mode === "dark"
      ? "rgba(30, 41, 59, 0.85)"
      : "background.paper",

  border: "1px solid",
  borderColor: (theme) =>
    theme.palette.mode === "dark"
      ? "rgba(255,255,255,0.08)"
      : "rgba(0,0,0,0.06)",

  boxShadow: (theme) =>
    theme.palette.mode === "dark"
      ? "0 8px 24px rgba(0,0,0,0.6)"
      : "0 6px 18px rgba(0,0,0,0.12)",

  backdropFilter: "blur(8px)",
  WebkitBackdropFilter: "blur(8px)",
};


  // Determine dashboard title based on role (filter cards only; top h5 uses greeting)
  const getDashboardTitle = () => {
    if (role === "Sales Rep") {
      return "My Performance Overview";
    } else if (role === "Manager") {
      return "Territory Performance Overview";
    } else {
      return "Company Performance Overview";
    }
  };

  const dashboardGreeting = useMemo(
    () => getDashboardGreeting(user),
    [user?.first_name, user?.full_name, user?.username]
  );

  // Check if filters should be visible/disabled
  const canViewTerritoryFilter = role !== "Sales Rep";
  const canViewUserFilter = role !== "Sales Rep";

  const leadDistributionTerritoryLabel = useMemo(() => {
    if (!selectedTerritory) return "Territory";
    const t = territories.find(
      (x) => String(x.id) === String(selectedTerritory)
    );
    return t?.name || "Territory";
  }, [selectedTerritory, territories]);

  const teamPerformanceUserLabel = useMemo(() => {
    if (!selectedUser) return "Rep";
    const u = users.find((x) => x.username === selectedUser);
    const name = (u?.full_name || u?.username || selectedUser || "").trim();
    return name || "Rep";
  }, [selectedUser, users]);

// =======================
// Team performance chart legend
// =======================
const CustomLegend = ({ payload }) => {
  if (!payload?.length) return null;
  return (
    <Box
      sx={{
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        justifyContent: "center",
        gap: 2,
        mt: 1.5,
        pt: 1,
        borderTop: "1px solid",
        borderColor: "divider",
      }}
    >
      {payload.map((entry, index) => (
        <Box
          key={`legend-${index}`}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.75,
            whiteSpace: "nowrap",
          }}
        >
          <Box
            sx={{
              width: 18,
              height: 3,
              borderRadius: 99,
              bgcolor: entry.color,
              flexShrink: 0,
            }}
          />
          <Typography variant="caption" color="text.secondary">
            {entry.value}
          </Typography>
        </Box>
      ))}
    </Box>
  );
};






  // ---------------- LOADERS ----------------

  const loadPerformanceData = useCallback(async () => {
    setLoading(true);
    try {
      // Build query params with ALL filters
      const params = new URLSearchParams();
      // Only add date params if not "All Time"
      if (start && end && selectedRange !== "allTime") {
        params.set("start", start);
        params.set("end", end);
        params.set("tz", dashboardTimeZone);
      }
      if (selectedTerritory) params.set("territory", selectedTerritory);
      if (selectedUser) {
        // Find the user ID from the users list
        const user = users.find(u => u.username === selectedUser);
        if (user) params.set("user", user.id);
      }

      const [summaryRes, seriesRes] = await Promise.all([
        api.get(`performance/summary/?${params.toString()}`),
        api.get(`performance/?group_by=week&${params.toString()}`),
      ]);
      

      // Extract backend data safely
      const leaderboardData = summaryRes.data.leaderboard || [];
      const totals = summaryRes.data.totals || {};
      const seriesData = seriesRes.data.series || [];

      // Update state
      setLeaderboard(leaderboardData);
      setSeries(seriesData);

      // Update Step 1 metrics from backend totals
      setMetrics((prev) => ({
        ...prev,
        total_leads: totals.total_leads || 0,
        active_leads: totals.active_leads || 0,
        leads_closed: totals.leads_closed || 0,
        follow_ups: totals.follow_ups || 0,
      }));

    } catch (err) {
      console.error("Performance fetch error:", err);
    } finally {
      setLoading(false);
    }
  }, [start, end, selectedRange, selectedTerritory, selectedUser, users, dashboardTimeZone]);

  const loadLeadStatus = useCallback(
    async (territoryId = "") => {
      try {
        const params = new URLSearchParams();
        if (territoryId) params.set("territory", territoryId);

        const repUser =
          selectedUser && users.length
            ? users.find((x) => x.username === selectedUser)
            : null;

        let res;
        if (role === "Sales Rep" && authUserId != null) {
          params.set("user", String(authUserId));
          if (start && end && selectedRange !== "allTime") {
            params.set("start_date", start);
            params.set("end_date", end);
            params.set("tz", dashboardTimeZone);
          }
          res = await api.get(
            `leads/status-activity-distribution/?${params.toString()}`
          );
        } else if (selectedUser && repUser) {
          // Rep selected: disposition outcomes from status-change logs (route-free), range-aware
          params.set("user", String(repUser.id));
          if (start && end && selectedRange !== "allTime") {
            params.set("start_date", start);
            params.set("end_date", end);
            params.set("tz", dashboardTimeZone);
          }
          res = await api.get(
            `leads/status-activity-distribution/?${params.toString()}`
          );
        } else {
          if (start && end && selectedRange !== "allTime") {
            params.set("start_date", start);
            params.set("end_date", end);
            params.set("tz", dashboardTimeZone);
            /* Company/manager view: status mix = leads the team touched in-range, not only created that day */
            params.set("activity_window", "1");
          }
          const qs = params.toString();
          res = await api.get(
            qs ? `leads/status-counts/?${qs}` : "leads/status-counts/"
          );
        }
        setStatusDistributionMeta({
          mode: res.data?.mode === "status_activity" ? "activity" : "snapshot",
          leadsTouched:
            typeof res.data?.leads_touched === "number"
              ? res.data.leads_touched
              : null,
        });
        const counts = res.data?.counts || {};
        const order = Array.isArray(res.data?.status_order)
          ? res.data.status_order
          : Object.keys(counts);

        const seen = new Set();
        const formatted = [];
        for (const name of order) {
          seen.add(name);
          const value = counts[name] ?? 0;
          const normalized = name?.toLowerCase().trim();
          const matchedColor =
            STATUS_COLORS[normalized] ||
            STATUS_COLORS[normalized.replaceAll("-", " ")] ||
            "#BDBDBD";
          formatted.push({ name, value, color: matchedColor });
        }
        for (const name of Object.keys(counts)) {
          if (!seen.has(name)) {
            const value = counts[name] ?? 0;
            const normalized = name?.toLowerCase().trim();
            const matchedColor =
              STATUS_COLORS[normalized] ||
              STATUS_COLORS[normalized.replaceAll("-", " ")] ||
              "#BDBDBD";
            formatted.push({ name, value, color: matchedColor });
          }
        }

        setStatusData(formatted);
      } catch (err) {
        console.error("Status chart fetch error:", err);
      }
    },
    [start, end, selectedRange, selectedUser, users, role, authUserId, dashboardTimeZone]
  );

  const chartStyles = useMemo(() => {
    const isDark = theme.palette.mode === "dark";
    const gridStroke = isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)";
    const tickFill = theme.palette.text.secondary;
    const tooltipBg = isDark ? "rgba(30, 41, 59, 0.96)" : "rgba(255,255,255,0.98)";
    const tooltipBorder = isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.08)";
    return {
      grid: { stroke: gridStroke },
      tooltip: {
        contentStyle: {
          backgroundColor: tooltipBg,
          border: `1px solid ${tooltipBorder}`,
          borderRadius: "12px",
          padding: "10px 14px",
          boxShadow: isDark
            ? "0 8px 24px rgba(0,0,0,0.45)"
            : "0 4px 20px rgba(0,0,0,0.08)",
        },
        labelStyle: {
          fontWeight: 600,
          marginBottom: 6,
          color: theme.palette.text.primary,
          fontSize: 12,
        },
        itemStyle: { fontSize: 12, paddingTop: 2 },
      },
      xAxis: {
        tick: { fill: tickFill, fontSize: 11 },
      },
      yAxis: {
        tick: { fill: tickFill, fontSize: 11 },
      },
      line: {
        activeDot: {
          r: 5,
          strokeWidth: 2,
          stroke: theme.palette.background.paper,
        },
      },
    };
  }, [theme]);

  const loadActivity = useCallback(async () => {
    try {
      const [notifRes, followupRes, extrasRes] = await Promise.all([
        api.get("notifications/"),
        api.get("followups/"),
        api.get("dashboard_activity_extras/"),
      ]);

      const notifs = (notifRes.data || [])
        .filter((n) => !n.is_read)
        .map((n) => ({
        id: `notif-${n.id}`,
        type: "notif",
        title: n.message,
        date: n.created_at,
        notifType: (n.type || "").toLowerCase(),
        // Activity feed notification rows should always open Message Center notifications inbox.
        target: "/message-center?filter=notifications",
      }));

      // Same lead-label logic as Message Center: name or address fallback
      const getLeadLabel = (f) => {
        if (f.lead_first_name || f.lead_last_name) {
          return `${f.lead_first_name || ""} ${f.lead_last_name || ""}`.trim();
        }
        return f.lead_address || f.lead_name || "Unknown Lead";
      };
      const followups = (followupRes.data || [])
        .filter((f) => !f.completed)
        .map((f) => {
          const leadLabel = getLeadLabel(f);
          return {
            id: `followup-${f.id}`,
            type: "followup",
            title: `Follow-up Scheduled for ${leadLabel}`,
            subtitle: f.due_date
              ? `Due: ${new Date(f.due_date).toLocaleDateString()}`
              : null,
            date: f.created_at,
            leadId: f.lead || null,
            target: f.lead ? `/leads/${f.lead}` : "/message-center?filter=followups",
          };
        });

      const mentions = extrasRes?.data?.mentions || [];
      const conversations = extrasRes?.data?.conversations || [];

      const merged = [...notifs, ...followups, ...mentions, ...conversations].sort(
        (a, b) => new Date(b.date) - new Date(a.date)
      );

      setActivityItems(merged.slice(0, 20));
    } catch (err) {
      console.error("Activity load error:", err);
    }
  }, []);

  const handleActivityOpen = useCallback(
    (item) => {
      if (!item) return;
      if (item.type === "mention") {
        setActivityItems((prev) => prev.filter((row) => row.type !== "mention"));
        api.post("notifications/mark-mentions-read/").catch((err) => {
          console.error("Mark mentions read failed:", err);
        });
        navigate("/team-chat");
        return;
      }
      const fallbackByType = {
        sms: "/message-center",
        followup: "/message-center?filter=followups",
        notif: "/message-center?filter=notifications",
      };
      const target = item.target || fallbackByType[item.type] || "/dashboard";
      if (typeof target === "string" && /^\/leads\/\d+/.test(target)) {
        navigate(target, {
          state: buildLeadDetailReturn({
            pathname: "/dashboard",
            label: "Dashboard",
          }),
        });
        return;
      }
      navigate(target);
    },
    [navigate]
  );

  const loadMetrics = useCallback(
    async (territoryId = "") => {
      try {
        // Only calculate activeReps here - all other metrics come from backend /performance/summary/
        const usersRes = await api.get("users/");
        const usersList = usersRes.data;

        // Active Reps — filtered by selected territory
        const activeReps = usersList.filter((u) => {
          const isSales =
            Array.isArray(u.groups) &&
            u.groups.some((g) => g.toLowerCase().includes("sales"));

          const matchesTerritory =
            !territoryId || // if no filter, include all
            u.territory?.id === Number(territoryId) ||
            u.territory_id === Number(territoryId) ||
            u.territory_name ===
              territories.find((t) => t.id === Number(territoryId))?.name;

          return u.is_active && isSales && matchesTerritory;
        }).length;

        setMetrics((prev) => ({
          ...prev,
          activeReps,
        }));
      } catch (err) {
        console.error("Metrics load error:", err);
      }
    },
    [territories]
  );

  const loadTerritories = useCallback(async () => {
    try {
      const res = await api.get("territories/");
      setTerritories(res.data || []);
    } catch (err) {
      console.error("Territories load error:", err);
    }
  }, []);

  const loadUsers = useCallback(async () => {
    try {
      const res = await api.get("users/");
      const filtered = res.data.filter(
        (u) =>
          Array.isArray(u.groups) &&
          u.groups.some(
            (g) =>
              g.toLowerCase().includes("sales") ||
              g.toLowerCase().includes("manager")
          )
      );
      setUsers(filtered);
    } catch (err) {
      console.error("Users load error:", err);
    }
  }, []);





  const handleRangeChange = (range) => {
    setSelectedRange(range);
    const now = new Date();

    switch (range) {
      case "allTime":
        // No date range - set to empty to indicate "all time"
        setStart("");
        setEnd("");
        break;
      case "today":
        setStart(formatLocalYMD(now));
        setEnd(formatLocalYMD(now));
        break;
      case "7":
        setStart(formatLocalYMD(addDays(now, -7)));
        setEnd(formatLocalYMD(now));
        break;
      case "30":
        setStart(formatLocalYMD(addDays(now, -30)));
        setEnd(formatLocalYMD(now));
        break;
      case "thisMonth":
        setStart(formatLocalYMD(startOfMonth(now)));
        setEnd(formatLocalYMD(endOfMonth(now)));
        break;
      case "lastMonth":
        const lastMonth = subMonths(now, 1);
        setStart(formatLocalYMD(startOfMonth(lastMonth)));
        setEnd(formatLocalYMD(endOfMonth(lastMonth)));
        break;
      case "thisYear":
        setStart(formatLocalYMD(startOfYear(now)));
        setEnd(formatLocalYMD(endOfYear(now)));
        break;
      case "custom":
      default:
        break;
    }
  };

  const handleResetFilters = () => {
    setSelectedRange("allTime");
    setStart("");
    setEnd("");
    setSelectedTerritory("");
    setSelectedUser("");
  };

  // ---------------- EFFECT ----------------
useEffect(() => {
  if (isMainNavTourComplete()) return;

  const steps = resolveDashboardSteps(role, {
    canAccessRoleSettings: canAccessRoleSettings(user, role),
  });
  if (!steps.length) return;

  const timer = setTimeout(() => {
    startTour(steps, MAIN_NAV_TOUR_KEY);
  }, 400);

  return () => clearTimeout(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [role, user]);


// ---------------- DATA LOAD (auto-apply on any filter change) ----------------
useEffect(() => {
  loadPerformanceData();
  loadLeadStatus(selectedTerritory);
  loadMetrics(selectedTerritory);
}, [start, end, selectedRange, selectedTerritory, selectedUser, loadPerformanceData, loadLeadStatus, loadMetrics]);

// ---------------- REFRESH ON TAB FOCUS ----------------
useEffect(() => {
  const refresh = () => {
    loadPerformanceData();
    loadLeadStatus(selectedTerritory);
    loadMetrics(selectedTerritory);
  };

  const onFocus = () => refresh();
  const onVisibility = () => {
    if (!document.hidden) refresh();
  };

  window.addEventListener("focus", onFocus);
  document.addEventListener("visibilitychange", onVisibility);

  return () => {
    window.removeEventListener("focus", onFocus);
    document.removeEventListener("visibilitychange", onVisibility);
  };
}, [
  loadPerformanceData,
  loadLeadStatus,
  loadMetrics,
  selectedTerritory,
]);

useEffect(() => {
  loadTerritories();
  loadUsers();
  loadActivity();

  const interval = setInterval(() => {
    loadActivity();
  }, 60000);
  return () => clearInterval(interval);
}, [
  loadTerritories,
  loadUsers,
  loadActivity,
]);

  // Listen for lead update events to refresh dashboard data
  useEffect(() => {
    const unsubscribe = subscribeLeadUpdates(() => {
      loadPerformanceData();
      loadLeadStatus(selectedTerritory);
      loadMetrics(selectedTerritory);
    });
    return () => unsubscribe();
  }, [loadPerformanceData, loadLeadStatus, loadMetrics, selectedTerritory]);

  if (loading)
    return <div className="p-6 text-center">Loading performance data...</div>;

  // Filter leaderboard by selected user
  const filteredLeaderboard = leaderboard.filter((r) => {
    if (!selectedUser) return true;
    return r.username === selectedUser;
  });

  // -------------------------------------------------- NEW: build chartData with "All" aggregation + bar-only series ----------
  const chartData =
  selectedUser === ""
    ? [
        {
          name: "All Reps",
          "Leads Added": leaderboard.reduce(
            (a, r) => a + (r.leads_added || 0),
            0
          ),
          "Leads Closed": leaderboard.reduce(
            (a, r) => a + (r.leads_closed || 0),
            0
          ),
          "Pending Leads": leaderboard.reduce(
            (a, r) => a + (r.leads_being_worked || 0),
            0
          ),
          "Conversion %":
            (() => {
              const totalAdded = leaderboard.reduce((a, r) => a + (r.leads_added || 0), 0);
              const totalClosed = leaderboard.reduce((a, r) => a + (r.leads_closed || 0), 0);
              return totalAdded > 0 ? Math.round((totalClosed / totalAdded) * 100) : 0;
            })(),
        },
      ]
    : filteredLeaderboard.map((r) => ({
        name: r.username,
        "Leads Added": r.leads_added ?? 0,
        "Leads Closed": r.leads_closed ?? 0,
        "Pending Leads": r.leads_being_worked ?? 0,
        "Conversion %": r.conversion_rate ?? 0,
      }));


  // ---------------- RENDER ----------------
  return (
    <Box sx={{ p: { xs: isLandscape ? 1 : 3, md: 3 }, px: { xs: 1, md: 3 } }}>
    <Box
      sx={{
        maxWidth: 1440,
        mx: "auto",
      }}
    >
      <Box
        sx={{
          mb: 2,
          borderRadius: "16px",
          px: { xs: 2.5, md: 3 },
          py: { xs: 2, md: 2.5 },
          background: `linear-gradient(135deg, ${DASHBOARD_HERO_BLUE_MAIN} 0%, ${DASHBOARD_HERO_BLUE_DARK} 100%)`,
          boxShadow: `0 10px 24px ${DASHBOARD_HERO_BLUE_MAIN}40`,
        }}
      >
        <Typography variant="h5" fontWeight={700} sx={{ color: "#fff" }}>
          {dashboardGreeting}
        </Typography>
        <Typography
          variant="caption"
          component="p"
          sx={{
            mt: 0.75,
            color: "rgba(255,255,255,0.85)",
            fontSize: "0.875rem",
            lineHeight: 1.45,
            fontWeight: 400,
          }}
        >
          Track your performance and lead metrics
        </Typography>
      </Box>

      {/* -------------------------------------------- PERFORMANCE OVERVIEW ---------------- */}
      <Box
        sx={{
          ...dashboardContainerSx,
          mb: 3,
          p: 2,
          borderRadius: 2,
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          flexWrap: "wrap",
          alignItems: { xs: "flex-start", md: "center" },
          justifyContent: "space-between",
          gap: { xs: 2, md: 2 },
         
        }}
      >
        {/* ---------------------------------------------------Desktop: Horizontal Layout------------------------ */}
        <Stack
          direction="row"
          spacing={2}
          alignItems="center"
          flexWrap="wrap"
          sx={{ 
            display: { xs: "none", md: "flex" },
            flex: 1,
            width: { md: "auto" },
          }}
        >
          <FilterAltIcon sx={{ color: "primary.main" }} />
          <Typography variant="subtitle1" fontWeight={600}>
            {getDashboardTitle()}
          </Typography>

          <TextField
            select
            size="small"
            label="Range"
            value={selectedRange}
            onChange={(e) => handleRangeChange(e.target.value)}
            SelectProps={{ native: true }}
            sx={{
              minWidth: 180,
              ml: 3,
              "& .MuiInputBase-root": { height: 36, fontSize: "0.9rem" },
            }}
          >
            {RANGE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </TextField>

          {selectedRange === "custom" && (
            <>
              <TextField
                type="date"
                size="small"
                value={start}
                onChange={(e) => setStart(e.target.value)}
                sx={{ minWidth: 150 }}
              />
              <TextField
                type="date"
                size="small"
                value={end}
                onChange={(e) => setEnd(e.target.value)}
                sx={{ minWidth: 150 }}
              />
            </>
          )}
        </Stack>

        {/* -----------------------------------------------------Desktop: Reset Filter Button ----------------- */}
        <Stack
          direction="row"
          spacing={1.5}
          alignItems="center"
          sx={{
            display: { xs: "none", md: "flex" },
            width: { md: "auto" },
            justifyContent: { md: "flex-end" },
          }}
        >
          <Button
            variant="outlined"
            color="primary"
            startIcon={<RefreshIcon />}
            onClick={handleResetFilters}
            sx={DASHBOARD_RESET_FILTER_BUTTON_SX}
          >
            Reset Filter
          </Button>
        </Stack>

        {/* ----------------------------------------------------------Mobile: Vertical Layout------------------------ */}
        {/* Mobile: ROW 1 – Icon + Subtitle */}
        <Stack
          direction="row"
          alignItems="center"
          spacing={1.2}
          sx={{
            display: { xs: "flex", md: "none" },
            width: "100%",
            mb: 1,
          }}
        >
          <FilterAltIcon sx={{ color: "primary.main" }} />
          <Typography variant="subtitle1" fontWeight={600}>
            {getDashboardTitle()}
          </Typography>
        </Stack>

        {/* ------------------------------------------------------------------Mobile: ROW 2 – Range Picker + Custom Dates */}
        <Stack
          direction="row"
          spacing={1.5}
          alignItems="center"
          flexWrap="wrap"
          sx={{
            display: { xs: "flex", md: "none" },
            width: "100%",
            mb: 1.2,
          }}
        >
          <TextField
            select
            size="small"
            label="Range"
            value={selectedRange}
            onChange={(e) => handleRangeChange(e.target.value)}
            SelectProps={{ native: true }}
            sx={{
              minWidth: { xs: "50%", md: 180 },
              "& .MuiInputBase-root": { height: 36, fontSize: "0.9rem" },
            }}
          >
            {RANGE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </TextField>

          {selectedRange === "custom" && (
            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={{ xs: 1.2, md: 1.5 }}
              sx={{
                flexBasis: "100%",
                mt: { xs: 1.5, md: 0 },
              }}
            >
              <TextField
                type="date"
                size="small"
                value={start}
                onChange={(e) => setStart(e.target.value)}
                sx={{
                  width: { xs: "50%", md: 150 },
                }}
              />
              <TextField
                type="date"
                size="small"
                value={end}
                onChange={(e) => setEnd(e.target.value)}
                sx={{
                  width: { xs: "50%", md: 150 },
                }}
              />
            </Stack>
          )}
        </Stack>

        {/* ------------------------------------------------------------------Mobile: Reset Filter Button */}
        <Stack
          direction="row"
          spacing={1.5}
          alignItems="center"
          sx={{
            display: { xs: "flex", md: "none" },
            width: "100%",
          }}
        >
          <Button
            variant="outlined"
            color="primary"
            startIcon={<RefreshIcon />}
            onClick={handleResetFilters}
            size="small"
            sx={DASHBOARD_RESET_FILTER_BUTTON_SX}
          >
            Reset Filter
          </Button>
        </Stack>
      </Box>

      {/* ------------------------------------------------------------------------------- METRIC CARDS (LIVE) ---------------- */}
      <Fade in={!loading} timeout={900}>
        <Box
          data-tour="metrics-cards"
          sx={{
            display: "grid",
            gap: { xs: isLandscape ? 1 : 1.5, md: 1.5 },
            gridTemplateColumns: {
              xs: isLandscape ? "repeat(4, minmax(120px, 1fr))" : "1fr",
              sm: "1fr 1fr",
              md: "repeat(3, 1fr)",
              lg: "repeat(5, 1fr)",
            },
            mb: { xs: isLandscape ? 2 : 4, md: 4 },
            mt: { xs: isLandscape ? 1 : 1.5, md: 1.5 },
            overflowX: isLandscape ? "auto" : "visible",
            pb: isLandscape ? 1 : 0,
          }}
        >
          <MetricCard
            label="Total Leads"
            value={metrics.total_leads}
            icon={<TrendingUpIcon sx={{ color: "primary.main", fontSize: 28 }} />}
          />
          <MetricCard
            label="Active"
            value={metrics.active_leads}
            icon={<TaskAltIcon sx={{ color: "success.main", fontSize: 28 }} />}
          />

          <MetricCard
            label="Closed"
            value={metrics.leads_closed}
            icon={<AttachMoneyIcon sx={{ color: "secondary.main", fontSize: 28 }} />}
          />
          <MetricCard
            label="Follow-ups"
            value={metrics.follow_ups}
            icon={<AccessTimeIcon sx={{ color: "warning.main", fontSize: 28 }} />}
          />

          <MetricCard
            label="Active Reps"
            value={metrics.activeReps}
            icon={<PeopleAltIcon sx={{ color: "info.main", fontSize: 28 }} />}
          />
        </Box>
      </Fade>

      {/* ---------------- DATA VISUALIZATION GRID ---------------- */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
          gap: { xs: isLandscape ? 1.5 : 3, md: 3 },
          mb: { xs: isLandscape ? 2 : 4, md: 4 },
        }}
      >
        {/* -------------------------------------------------------Lead Status Distribution */}
        <Card
          data-tour="lead-status-distribution"
          sx={{ 
  ...dashboardContainerSx,
}}>
          <CardContent
            sx={{ display: "flex", flexDirection: "column", height: "100%" }}
          >
            {/* ----------------------------------------------------------Header */}
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="flex-start"
              sx={{ mb: 1 }}
            >
              <Box sx={{ minWidth: 0, flex: 1, pr: 1 }}>
                <Typography variant="subtitle1" fontWeight={700}>
                  Lead Status Distribution
                </Typography>
                {statusDistributionMeta.mode === "activity" ? (
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5, maxWidth: 420 }}>
                    {role === "Sales Rep"
                      ? "Dispositions you set in the selected period."
                      : "Dispositions this rep set in the selected period."}
                    {statusDistributionMeta.leadsTouched != null
                      ? ` ${statusDistributionMeta.leadsTouched} lead${statusDistributionMeta.leadsTouched === 1 ? "" : "s"} with a status change.`
                      : ""}
                  </Typography>
                ) : (
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5, maxWidth: 420 }}>
                    {canViewUserFilter
                      ? "Select a rep in Team performance to see dispositions from their updates."
                      : "Current pipeline mix in scope."}
                  </Typography>
                )}
              </Box>

              {/* -------------------------------------------------------Territory filter (button + popover) */}
              {canViewTerritoryFilter && (
                <>
                  <Button
                    data-tour="lead-status-territory-filter"
                    variant="outlined"
                    color="primary"
                    size="small"
                    startIcon={<FilterListIcon />}
                    aria-haspopup="true"
                    aria-expanded={
                      Boolean(leadStatusTerritoryAnchorEl) ? "true" : undefined
                    }
                    onClick={(e) =>
                      setLeadStatusTerritoryAnchorEl(e.currentTarget)
                    }
                    sx={{ ...DASHBOARD_SCOPE_FILTER_BUTTON_SX }}
                  >
                    <Typography
                      component="span"
                      noWrap
                      sx={{ maxWidth: { xs: 240, sm: 200 } }}
                    >
                      {leadDistributionTerritoryLabel}
                    </Typography>
                  </Button>
                  <Popover
                    open={Boolean(leadStatusTerritoryAnchorEl)}
                    anchorEl={leadStatusTerritoryAnchorEl}
                    onClose={() => setLeadStatusTerritoryAnchorEl(null)}
                    anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                    transformOrigin={{ vertical: "top", horizontal: "right" }}
                    slotProps={{
                      paper: { sx: DASHBOARD_SCOPE_FILTER_POPOVER_PAPER_SX },
                    }}
                  >
                    <Box sx={{ px: 2, pt: 1.5, pb: 0.5 }}>
                      <Typography variant="subtitle2" fontWeight={700}>
                        Territory
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        display="block"
                        sx={{ mb: 0.5 }}
                      >
                        Filter lead status by territory
                      </Typography>
                    </Box>
                    <List
                      dense
                      disablePadding
                      sx={{ maxHeight: 280, overflow: "auto", pb: 1 }}
                    >
                      <ListItemButton
                        selected={!selectedTerritory}
                        onClick={() => {
                          setSelectedTerritory("");
                          setLeadStatusTerritoryAnchorEl(null);
                        }}
                      >
                        <ListItemText primary="All territories" />
                      </ListItemButton>
                      {territories.map((t) => (
                        <ListItemButton
                          key={t.id}
                          selected={
                            String(selectedTerritory) === String(t.id)
                          }
                          onClick={() => {
                            setSelectedTerritory(String(t.id));
                            setLeadStatusTerritoryAnchorEl(null);
                          }}
                        >
                          <ListItemText primary={t.name} />
                        </ListItemButton>
                      ))}
                    </List>
                  </Popover>
                </>
              )}
            </Stack>

            {/* ------------------------------------------------------------Status Bar List */}
            <Box>
              {(() => {
                const total = statusData.reduce((sum, d) => sum + d.value, 0);
                return statusData.map((s) => {
                  const percent = total ? (s.value / total) * 100 : 0;
                  const percentLabel = percent.toFixed(1);
                  return (
                    <Stack
                      key={s.name}
                      direction="row"
                      alignItems="center"
                      spacing={1.5}
                      sx={{ mb: 1 }}
                    >
                      {/* Status name */}
                      <Typography
                        variant="body2"
                        sx={{ minWidth: 140, fontWeight: 500, color: "text.primary", flexShrink: 0 }}
                      >
                        {s.name}
                      </Typography>

                      {/* Progress bar track */}
                      <Box
                        sx={{
                          flex: 1,
                          height: 8,
                          borderRadius: 99,
                          bgcolor: (theme) =>
                            theme.palette.mode === "dark"
                              ? "rgba(255,255,255,0.08)"
                              : "rgba(0,0,0,0.07)",
                          overflow: "hidden",
                        }}
                      >
                        <Box
                          sx={{
                            width: `${percent}%`,
                            height: "100%",
                            borderRadius: 99,
                            bgcolor: s.color,
                            transition: "width 0.6s ease",
                            minWidth: percent > 0 ? 6 : 0,
                          }}
                        />
                      </Box>

                      {/* Count */}
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 600,
                          color: "text.primary",
                          fontVariantNumeric: "tabular-nums",
                          minWidth: 32,
                          textAlign: "right",
                          flexShrink: 0,
                        }}
                      >
                        {s.value.toLocaleString()}
                      </Typography>

                      {/* Percent */}
                      <Typography
                        variant="caption"
                        sx={{
                          color: "text.secondary",
                          fontVariantNumeric: "tabular-nums",
                          minWidth: 38,
                          textAlign: "right",
                          flexShrink: 0,
                        }}
                      >
                        {percentLabel}%
                      </Typography>
                    </Stack>
                  );
                });
              })()}
            </Box>
          </CardContent>
        </Card>

        {/*------------------------------------------------------------- Rep Performance (Leaderboard with Conversion & Dropdown) */}
        <Card
          data-tour="rep-performance"
          sx={{
            ...dashboardContainerSx,
            display: "flex",
            flexDirection: "column",
            minHeight: { xs: isLandscape ? 300 : 400, md: 400 },
            height: "100%",
          }}
        >
          <CardContent
            sx={{
              display: "flex",
              flexDirection: "column",
              flex: 1,
              minHeight: 0,
              pt: 2,
              pb: "16px !important",
              "&:last-child": { pb: "16px !important" },
            }}
          >
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="flex-start"
              sx={{ mb: 1, flexShrink: 0 }}
            >
              <Typography
                variant="subtitle1"
                fontWeight={700}
                sx={{ minWidth: 0, flex: 1, pr: 1 }}
              >
                {selectedUser
                  ? `${selectedUser}'s Performance`
                  : role === "Sales Rep"
                    ? "My Performance"
                    : "Team Performance"}
              </Typography>
              {canViewUserFilter && (
                <>
                  <Button
                    data-tour="rep-performance-filter"
                    variant="outlined"
                    color="primary"
                    size="small"
                    startIcon={<FilterListIcon />}
                    aria-haspopup="true"
                    aria-expanded={
                      Boolean(teamPerformanceUserAnchorEl) ? "true" : undefined
                    }
                    onClick={(e) =>
                      setTeamPerformanceUserAnchorEl(e.currentTarget)
                    }
                    sx={{ ...DASHBOARD_SCOPE_FILTER_BUTTON_SX }}
                  >
                    <Typography
                      component="span"
                      noWrap
                      sx={{ maxWidth: { xs: 240, sm: 200 } }}
                    >
                      {teamPerformanceUserLabel}
                    </Typography>
                  </Button>
                  <Popover
                    open={Boolean(teamPerformanceUserAnchorEl)}
                    anchorEl={teamPerformanceUserAnchorEl}
                    onClose={() => setTeamPerformanceUserAnchorEl(null)}
                    anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                    transformOrigin={{ vertical: "top", horizontal: "right" }}
                    slotProps={{
                      paper: { sx: DASHBOARD_SCOPE_FILTER_POPOVER_PAPER_SX },
                    }}
                  >
                    <Box sx={{ px: 2, pt: 1.5, pb: 0.5 }}>
                      <Typography variant="subtitle2" fontWeight={700}>
                        Rep
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        display="block"
                        sx={{ mb: 0.5 }}
                      >
                        Filter team performance
                      </Typography>
                    </Box>
                    <List
                      dense
                      disablePadding
                      sx={{ maxHeight: 280, overflow: "auto", pb: 1 }}
                    >
                      <ListItemButton
                        selected={!selectedUser}
                        onClick={() => {
                          setSelectedUser("");
                          setTeamPerformanceUserAnchorEl(null);
                        }}
                      >
                        <ListItemText primary="All reps" />
                      </ListItemButton>
                      {users.map((u) => (
                        <ListItemButton
                          key={u.id}
                          selected={selectedUser === u.username}
                          onClick={() => {
                            setSelectedUser(u.username);
                            setTeamPerformanceUserAnchorEl(null);
                          }}
                        >
                          <ListItemText
                            primary={u.full_name || u.username}
                          />
                        </ListItemButton>
                      ))}
                    </List>
                  </Popover>
                </>
              )}
            </Stack>

            <Box
              sx={{
                flex: 1,
                minHeight: { xs: 320, md: 280 },
                minWidth: 0,
              }}
            >
              <ResponsiveContainer width="100%" height="100%">
                <LineChart
                  data={series}
                  margin={{ top: 12, right: 12, left: 4, bottom: 12 }}
                >
                  <CartesianGrid
                    vertical={false}
                    strokeDasharray="4 8"
                    stroke={chartStyles.grid.stroke}
                  />
                  <XAxis
                    dataKey="date"
                    tick={chartStyles.xAxis.tick}
                    axisLine={false}
                    tickLine={false}
                    tickMargin={10}
                    interval="preserveStartEnd"
                    minTickGap={36}
                    tickFormatter={(value) => {
                      if (!value) return "";
                      const d = new Date(`${value}T12:00:00`);
                      return Number.isNaN(d.getTime())
                        ? String(value)
                        : d.toLocaleDateString(undefined, {
                            month: "short",
                            day: "numeric",
                          });
                    }}
                  />
                  <YAxis
                    tick={chartStyles.yAxis.tick}
                    axisLine={false}
                    tickLine={false}
                    width={44}
                    tickMargin={8}
                  />
                  <Tooltip
                    contentStyle={chartStyles.tooltip.contentStyle}
                    labelStyle={chartStyles.tooltip.labelStyle}
                    itemStyle={chartStyles.tooltip.itemStyle}
                  />
                  <Legend
                    wrapperStyle={{ paddingTop: 0 }}
                    iconType="line"
                    content={<CustomLegend />}
                  />

                  <Line
                    type="monotone"
                    dataKey="leads_added"
                    name="Leads Added"
                    stroke="#42A5F5"
                    strokeWidth={2.25}
                    dot={false}
                    activeDot={chartStyles.line.activeDot}
                    connectNulls
                    isAnimationActive={false}
                  />
                  <Line
                    type="monotone"
                    dataKey="leads_closed"
                    name="Leads Closed"
                    stroke="#43A047"
                    strokeWidth={2.25}
                    dot={false}
                    activeDot={chartStyles.line.activeDot}
                    connectNulls
                    isAnimationActive={false}
                  />
                  <Line
                    type="monotone"
                    dataKey="leads_being_worked"
                    name="Being Worked"
                    stroke="#FB8C00"
                    strokeWidth={2.25}
                    dot={false}
                    activeDot={chartStyles.line.activeDot}
                    connectNulls
                    isAnimationActive={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            </Box>
          </CardContent>
        </Card>

        {/* --------------------------------------------------------------------------------- ACTIVITY FEED ---------------- */}
        <Card
          data-tour="notifications"
          sx={{ ...dashboardContainerSx, height: { xs: isLandscape ? 300 : 400, md: 400 } }}
        >
          <CardContent sx={{ display: "flex", flexDirection: "column", height: "100%", pb: "12px !important" }}>
            {/* Header */}
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1, flexShrink: 0 }}>
              <BoltIcon color="primary" />
              <Typography variant="subtitle1" fontWeight={700}>
                Activity
              </Typography>
            </Stack>
            <Divider sx={{ mb: 1.5, flexShrink: 0 }} />

            {/* Scrollable feed */}
            <Box sx={{ flex: 1, overflowY: "auto", pr: 0.5 }}>
              {activityItems.length > 0 ? (
                <Stack spacing={1}>
                  {activityItems.map((item) => {
                    const isFollowup = item.type === "followup";
                    const isMention = item.type === "mention";
                    const isSms = item.type === "sms";
                    const chipLabel = isFollowup
                      ? "FOLLOW-UP"
                      : isMention
                      ? "MENTION"
                      : isSms
                      ? "SMS"
                      : "NOTIFICATION";
                    const chipColor = isFollowup
                      ? { bg: "rgba(245,124,0,0.14)", color: "#F57C00" }
                      : isMention
                      ? { bg: "rgba(124,58,237,0.12)", color: "#7c3aed" }
                      : isSms
                      ? { bg: "rgba(14,165,233,0.12)", color: "#0284c7" }
                      : { bg: "rgba(26,115,232,0.12)", color: "#1A73E8" };
                    const Icon = isFollowup
                      ? EventNoteIcon
                      : isMention
                      ? ForumOutlinedIcon
                      : isSms
                      ? SmsOutlinedIcon
                      : NotificationsActiveIcon;

                    return (
                      <Stack
                        key={item.id}
                        direction="row"
                        alignItems="flex-start"
                        spacing={1.5}
                        role="button"
                        tabIndex={0}
                        onClick={() => handleActivityOpen(item)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            handleActivityOpen(item);
                          }
                        }}
                        sx={{
                          p: 1.25,
                          borderRadius: 2,
                          border: "1px solid",
                          cursor: "pointer",
                          transition: "transform 0.15s ease, box-shadow 0.15s ease, background-color 0.15s ease",
                          borderColor: (theme) =>
                            theme.palette.mode === "dark"
                              ? "rgba(255,255,255,0.07)"
                              : "rgba(0,0,0,0.06)",
                          bgcolor: (theme) =>
                            theme.palette.mode === "dark"
                              ? "rgba(255,255,255,0.03)"
                              : "background.default",
                          "&:hover": {
                            transform: "translateY(-1px)",
                            boxShadow: (theme) =>
                              theme.palette.mode === "dark"
                                ? "0 8px 20px rgba(0,0,0,0.35)"
                                : "0 8px 18px rgba(0,0,0,0.08)",
                            bgcolor: (theme) =>
                              theme.palette.mode === "dark"
                                ? "rgba(255,255,255,0.06)"
                                : "rgba(0,0,0,0.03)",
                          },
                        }}
                      >
                        {/* Type pill */}
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.4,
                            px: 1,
                            py: 0.3,
                            borderRadius: 99,
                            bgcolor: chipColor.bg,
                            flexShrink: 0,
                          }}
                        >
                          <Icon sx={{ fontSize: 12, color: chipColor.color }} />
                          <Typography
                            sx={{
                              fontSize: "0.65rem",
                              fontWeight: 700,
                              color: chipColor.color,
                              letterSpacing: "0.04em",
                              lineHeight: 1,
                            }}
                          >
                            {chipLabel}
                          </Typography>
                        </Box>

                        {/* Content */}
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: 500, lineHeight: 1.3, mb: item.subtitle ? 0.3 : 0 }}
                            noWrap
                          >
                            {item.title}
                          </Typography>
                          {item.subtitle && (
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.7rem" }}>
                              {item.subtitle}
                            </Typography>
                          )}
                        </Box>

                        {/* Timestamp */}
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ fontSize: "0.68rem", flexShrink: 0, pt: 0.2 }}
                        >
                          {new Date(item.date).toLocaleDateString()}
                        </Typography>
                      </Stack>
                    );
                  })}
                </Stack>
              ) : (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ textAlign: "center", mt: 8 }}
                >
                  No recent activity
                </Typography>
              )}
            </Box>
          </CardContent>
        </Card>
        {/* --------------------------------------------------------------------- PERFORMANCE SNAPSHOT (REP OR TEAM) ---------------- */}
        <Card
          data-tour="team-performance-snapshot"
          sx={{ 
...dashboardContainerSx,
height: { xs: isLandscape ? 300 : 400, md: 400 } }}>
          <CardContent>
            <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2 }}>
              {selectedUser
                ? `${selectedUser}'s Performance Snapshot`
                : role === "Sales Rep"
                  ? "My Performance Snapshot"
                  : "Team Performance Snapshot"}
            </Typography>

            <Box
              sx={{
                display: "grid",
                gap: 2,
                gridTemplateColumns: {
                  xs: "1fr 1fr",
                  sm: "1fr 1fr",
                  md: "1fr 1fr",
                  lg: "1fr 1fr",
                },
                gridTemplateRows: {
                  xs: "auto auto",
                  sm: "auto auto",
                },
                alignItems: "stretch",
                justifyItems: "stretch",
                mb: 2,
              }}
            >
              {(() => {
                let rep;
                
                if (selectedUser) {
                  // Single rep selected - use leaderboard data
                  rep = leaderboard.find((r) => r.username === selectedUser);
                  if (!rep) {
                    // Fallback if user not found
                    rep = {
                      leads_added: 0,
                      leads_closed: 0,
                      leads_being_worked: 0,
                      conversion_rate: 0,
                    };
                  }
                } else if (role === "Sales Rep") {
                  const uid = user?.id != null ? Number(user.id) : null;
                  const selfRow = leaderboard.find(
                    (r) => uid != null && Number(r.user_id) === uid
                  );
                  const added = selfRow?.leads_added ?? 0;
                  rep = {
                    leads_added: added,
                    leads_closed: metrics.leads_closed,
                    leads_being_worked: metrics.active_leads,
                    conversion_rate:
                      added > 0
                        ? Math.round((metrics.leads_closed / added) * 100)
                        : 0,
                  };
                } else {
                  // No user selected - use territory-scoped totals from backend
                  rep = {
                    leads_added: leaderboard.reduce(
                      (a, r) => a + (r.leads_added || 0),
                      0
                    ),
                    leads_closed: metrics.leads_closed,
                    leads_being_worked: metrics.active_leads,
                    conversion_rate: metrics.total_leads > 0
                      ? Math.round((metrics.leads_closed / metrics.total_leads) * 100)
                      : 0,
                  };
                }

                return (
                  <>
                    <MetricCard
                      label="Leads Added"
                      value={rep?.leads_added ?? 0}
                      icon={<TrendingUpIcon color="primary" />}
                    />
                    <MetricCard
                      label="Leads Closed"
                      value={rep?.leads_closed ?? 0}
                      icon={<TaskAltIcon color="success" />}
                    />
			
                    <MetricCard
                      label="Leads Being Worked"
                      value={rep?.leads_being_worked ?? 0}
                      icon={<AccessTimeIcon color="info" />}
                    />
		
                    <MetricCard
                      label="Conversion Rate"
                      value={`${rep?.conversion_rate ?? 0}%`}
                      icon={<QueryStatsIcon color="secondary" />}
                    />
                  </>
                );
              })()}
            </Box>

            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mt: 1, display: "block" }}
            >
              {selectedUser
                ? `Snapshot of ${selectedUser}'s performance this period.`
                : role === "Sales Rep"
                  ? "Leads added from routes you built; closed and in progress match the summary cards (your assigned leads)."
                  : "Aggregated metrics across all visible reps."}
            </Typography>
          </CardContent>
        </Card>
      </Box>
    </Box>
</Box>
  );
}